import { Injectable } from "@angular/core"
import { Sort } from "@angular/material/sort"
import { ComponentStore, tapResponse } from "@ngrx/component-store"
import { Observable, of } from "rxjs"
import { debounceTime, distinctUntilChanged, filter, map, switchMap, take, tap } from "rxjs/operators"
import {
    ComparableListRequest,
    ValuationComparableService
} from "src/app/core/valuation-process/_services/valuation-comparable.service"
import { buildFilters, ColumnFilter, toRemoteFilters } from "./filter"
import { ComparableListItem, mapRemoteToListItem } from "./list-item"

export const DEFAULT_SORT: Sort = { active: 'date', direction: 'desc' }
export const PAGE_SIZE = 20

/** Bursts of state changes (typing, a paginator held down) collapse into one request. */
const REQUEST_DEBOUNCE_MS = 150

interface State {
    /** Property types chosen in the map's filter section — the table honours the same set. */
    propertySubTypeIds: number[],
    search: string,
    sort: Sort,
    pageIndex: number,
    filters: ColumnFilter[],
    /** The property-type set `filters` value lists were built for; null before any fetch. */
    facetsKey: string | null,
    /** Selected comparables — the server orders these ahead of the rest. */
    pinnedRefNums: string[],
    items: ComparableListItem[],
    total: number,
    loading: boolean
}

const INITIAL_STATE: State = {
    propertySubTypeIds: [],
    search: null,
    sort: DEFAULT_SORT,
    pageIndex: 0,
    filters: [],
    facetsKey: null,
    pinnedRefNums: [],
    items: [],
    total: 0,
    loading: false
}

function subTypeKey(ids: number[]): string {
    return [...(ids || [])].sort((a, b) => a - b).join(',')
}

/**
 * Query state for the comparables table, and the fetching that follows from it.
 *
 * The table is server-driven: filtering, searching, sorting and paging are all expressed
 * as a request, and only the page on screen is ever held here. Provided at the module
 * level so it outlives the component — leaving the tab and coming back restores the same
 * page, sort and filters rather than starting over.
 */
@Injectable()
export class ComparableListStore extends ComponentStore<State> {
    readonly items$ = this.select(state => state.items)
    readonly total$ = this.select(state => state.total)
    readonly loading$ = this.select(state => state.loading)
    readonly sort$ = this.select(state => state.sort)
    readonly pageIndex$ = this.select(state => state.pageIndex)
    readonly search$ = this.select(state => state.search)
    readonly pageSize = PAGE_SIZE

    /**
     * The request the current state describes. Distinct-checked on its serialised form so
     * that state changes which don't alter the query (the arriving rows, the loading flag)
     * don't refetch.
     *
     * The order matters. Deduplicating first, against the last query rather than the last
     * request sent, means a query that was skipped -- an empty sub-type set, or one the
     * debounce swallowed -- still counts as a change, so returning to an earlier query
     * fetches again rather than leaving the rows cleared and "Loading..." up for good. And
     * only real query changes reach the debounce, so unrelated state churn can't hold it off.
     */
    private readonly request$: Observable<ComparableListRequest> = this.state$.pipe(
        tap(state => console.log('[cmp-debug] state$', state.propertySubTypeIds, 'loading', state.loading)), // TEMP DEBUG
        map(state => ({ state, request: toRequest(state) })),
        map(entry => ({ ...entry, key: JSON.stringify(entry.request) })),
        distinctUntilChanged((a, b) => a.key === b.key),
        tap(entry => console.log('[cmp-debug] query changed', entry.key)), // TEMP DEBUG
        // Nothing is listed until the map's property-type filter has been applied, which
        // is also what stops the first render from asking for every type at once.
        filter(entry => entry.state.propertySubTypeIds.length > 0),
        debounceTime(REQUEST_DEBOUNCE_MS),
        tap(entry => console.log('[cmp-debug] after debounce -> load', entry.key)), // TEMP DEBUG
        map(entry => entry.request)
    )

    constructor(private service: ValuationComparableService) {
        super(INITIAL_STATE)
        this.load(this.request$)
    }

    /** The property types to list. Changing them resets the page and the column filters. */
    setPropertySubTypeIds(propertySubTypeIds: number[]) {
        console.log('[cmp-debug] setPropertySubTypeIds', propertySubTypeIds) // TEMP DEBUG
        this.patchState(state => {
            console.log('[cmp-debug]   current', state.propertySubTypeIds, 'same?', subTypeKey(state.propertySubTypeIds) == subTypeKey(propertySubTypeIds)) // TEMP DEBUG
            if (subTypeKey(state.propertySubTypeIds) == subTypeKey(propertySubTypeIds)) {
                return state
            }
            // The old filters name values of types that are no longer listed, so keeping
            // them would silently hide rows of the types just switched to.
            //
            // The rows go with them: they are comparables of the outgoing types, and the
            // fetch that replaces them is a round trip away. Holding them on screen means
            // a Mixed Use segment shows the previous segment's comparables until its own
            // arrive.
            return {
                ...state,
                propertySubTypeIds,
                filters: [],
                facetsKey: null,
                pageIndex: 0,
                items: [],
                total: 0,
                loading: true
            }
        })
    }

    setPinnedRefNums(pinnedRefNums: string[]) {
        this.patchState({ pinnedRefNums })
    }

    setSearch(search: string) {
        this.patchState({ search, pageIndex: 0 })
    }

    setSort(sort: Sort) {
        this.patchState({ sort, pageIndex: 0 })
    }

    setPageIndex(pageIndex: number) {
        this.patchState({ pageIndex })
    }

    setFilters(filters: ColumnFilter[]) {
        this.patchState({ filters, pageIndex: 0 })
    }

    /** Clears the search and sort and re-ticks every value in every column. */
    reset() {
        this.patchState(state => ({
            ...state,
            search: null,
            sort: DEFAULT_SORT,
            pageIndex: 0,
            filters: state.filters.map(filter => ({
                ...filter,
                selectedValues: [...filter.values],
                range: { min: null, max: null },
                month: null,
                year: null
            }))
        }))
    }

    /**
     * The column filters for the dialog, fetching the value lists the first time they are
     * needed for the current property types. The lists come from the server because the
     * rows they describe are no longer all in the browser.
     */
    filtersForDialog(): Observable<ColumnFilter[]> {
        return this.state$.pipe(
            take(1),
            switchMap(state => {
                const key = subTypeKey(state.propertySubTypeIds)
                if (state.facetsKey === key) {
                    return of(state.filters)
                }
                return this.service.listFilters(state.propertySubTypeIds).pipe(
                    map(facets => {
                        const filters = buildFilters(facets, state.filters)
                        this.patchState({ filters, facetsKey: key })
                        return filters
                    })
                )
            })
        )
    }

    /** Refetch the current page — after an upload, or anything else that adds rows. */
    refresh() {
        this.state$.pipe(take(1)).subscribe(state => {
            if (state.propertySubTypeIds.length == 0) {
                return
            }
            this.load(toRequest(state))
        })
    }

    private readonly load = this.effect<ComparableListRequest>(request$ =>
        request$.pipe(
            tap(request => console.log('[cmp-debug] load effect: sending', request)), // TEMP DEBUG
            tap(() => this.patchState({ loading: true })),
            switchMap(request =>
                this.service.list(request).pipe(
                    tapResponse(
                        page => this.patchState({
                            items: page.items.map(mapRemoteToListItem),
                            total: page.total,
                            // The server clamps a page index that has fallen off the end.
                            pageIndex: page.pageNumber,
                            loading: false
                        }),
                        (err) => {
                            console.log('[cmp-debug] load error', err) // TEMP DEBUG
                            console.error(err)
                            this.patchState({ items: [], total: 0, loading: false })
                        }
                    )
                )
            )
        )
    )
}

function toRequest(state: State): ComparableListRequest {
    // An empty direction is Material's "no sort"; leaving the field off lets the server
    // fall back to its stable ref-num order rather than inventing an ascending one.
    const sorted = state.sort && state.sort.direction != ''
    return {
        filter: {
            propertySubTypeIds: state.propertySubTypeIds,
            search: state.search ?? '',
            columns: toRemoteFilters(state.filters)
        },
        sortField: sorted ? state.sort.active : null,
        sortOrder: sorted ? state.sort.direction : null,
        pageNumber: state.pageIndex,
        pageSize: PAGE_SIZE,
        pinnedRefNums: state.pinnedRefNums
    }
}
